import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';

export default function Team() {
  const { user, canWrite } = useAuth();
  const [team, setTeam]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ name:'', email:'', password:'', role:'member', designation:'', department:'', phone:'', employment:'active' });
  const [saving, setSaving]     = useState(false);

  // Brief credentials popup shown once after creating a member
  const [createdCreds, setCreatedCreds] = useState(null);
  const [copied, setCopied]             = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const r = await api.get('/team'); setTeam(r.data.data); }
    catch {} finally { setLoading(false); }
  };

  const updateAvailability = async (id, availability) => {
    try { await api.put(`/team/${id}/availability`, { availability }); load(); } catch {}
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post('/users', form);
      const m = r.data.data;
      setShowForm(false);
      setForm({ name:'', email:'', password:'', role:'member', designation:'', department:'', phone:'', employment:'active' });
      load();
      if (m.plain_password) {
        setCreatedCreds({ name: m.name, email: m.email, password: m.plain_password });
      }
    } catch {} finally { setSaving(false); }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 1500);
    });
  };

  const isOwner = user?.role === 'owner';

  const available = team.filter(m => m.availability === 'available');
  const busy      = team.filter(m => m.availability === 'busy');
  const absent    = team.filter(m => m.availability === 'absent');

  if (loading) return <div className="page-wrap"><div className="empty-state"><div className="empty-state-title">Loading...</div></div></div>;

  return (
    <div className="page-wrap">
      {/* Summary chips */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        <span style={chipStyle('#22c55e', '#f0fdf4')}>✅ Available: {available.length}</span>
        <span style={chipStyle('#f59e0b', '#fffbeb')}>⏸ Busy: {busy.length}</span>
        <span style={chipStyle('#ef4444', '#fef2f2')}>✕ Absent: {absent.length}</span>
      </div>

      {/* Cards grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:16 }}>
        {team.map(m => {
          const avail = m.availability || 'available';
          const { dot, badge, badgeBg } = statusStyle(avail);
          const canChange = isOwner || user?.id === m.id || canWrite('page:team');

          return (
            <div key={m.id} style={{
              background:'var(--card)',
              border:'1px solid var(--border)',
              borderRadius:12,
              padding:'18px 16px 14px',
              display:'flex',
              flexDirection:'column',
              alignItems:'center',
              gap:8,
              position:'relative',
              boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
            }}>
              {/* Status dot */}
              <span style={{
                position:'absolute', top:12, right:12,
                width:10, height:10, borderRadius:'50%',
                background: dot, boxShadow:`0 0 0 2px var(--card), 0 0 0 3px ${dot}55`,
              }} title={avail} />

              {/* Avatar */}
              <div style={{
                width:56, height:56, borderRadius:'50%',
                background: m.color || 'var(--primary)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'1.2rem', fontWeight:800, color:'#fff',
                flexShrink:0, letterSpacing:1,
              }}>
                {m.initials || (m.name||'').slice(0,2).toUpperCase()}
              </div>

              {/* Name + role */}
              <div style={{ textAlign:'center' }}>
                <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text)', lineHeight:1.3 }}>{m.name}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>{m.designation || m.role}</div>
              </div>

              {/* Status badge */}
              <span style={{
                fontSize:'0.68rem', fontWeight:700, padding:'3px 10px',
                borderRadius:20, background: badgeBg, color: badge,
                textTransform:'capitalize', letterSpacing:0.3,
              }}>
                {avail.charAt(0).toUpperCase() + avail.slice(1)}
              </span>

              {/* Employment badge */}
              {m.employment && m.employment !== 'inactive' && (
                <span style={{
                  fontSize:'0.65rem', fontWeight:600, padding:'2px 8px',
                  borderRadius:20, background:'#dcfce7', color:'#16a34a',
                  letterSpacing:0.3,
                }}>
                  Active Employee
                </span>
              )}

              {/* Active tasks */}
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>
                {m.active_tasks ?? 0} active tasks
              </div>

              {/* Availability action buttons */}
              {canChange && (
                <div style={{ display:'flex', gap:6, marginTop:4 }}>
                  <button title="Set Available" onClick={() => updateAvailability(m.id, 'available')} style={actionBtn(avail === 'available', '#22c55e')}>✓</button>
                  <button title="Set Busy"      onClick={() => updateAvailability(m.id, 'busy')}      style={actionBtn(avail === 'busy',      '#f59e0b')}>⏸</button>
                  <button title="Set Absent"    onClick={() => updateAvailability(m.id, 'absent')}    style={actionBtn(avail === 'absent',    '#ef4444')}>✕</button>
                </div>
              )}
            </div>
          );
        })}

        {team.length === 0 && (
          <div className="empty-state" style={{ gridColumn:'1/-1' }}>
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-title">No team members yet</div>
          </div>
        )}
      </div>

      {/* ── Add Member Modal ── */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <div className="modal-title">+ Add Team Member</div>
              <button className="modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={save}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input className="form-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                      <option value="manager">Manager</option>
                      <option value="member">Member</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <input className="form-input" type="text" placeholder="Leave blank to auto-generate" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input className="form-input" value={form.designation} onChange={e => setForm({...form, designation: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <input className="form-input" value={form.department} onChange={e => setForm({...form, department: e.target.value})} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Employment</label>
                    <select className="form-select" value={form.employment} onChange={e => setForm({...form, employment: e.target.value})}>
                      <option value="active">Full Time</option>
                      <option value="part-time">Part Time</option>
                      <option value="freelance">Freelance</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer" style={{ padding:'0', border:'none', marginTop:'8px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add Member'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Credentials Popup (shown once after create) ── */}
      {createdCreds && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth:400 }}>
            <div className="modal-header">
              <div className="modal-title">🔑 Login Credentials — {createdCreds.name}</div>
              <button className="modal-close" onClick={() => setCreatedCreds(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:14 }}>
                Share these credentials with the team member. To manage passwords later, go to <strong>Settings → Security</strong>.
              </p>
              <div style={credRow}>
                <span style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:70 }}>Email</span>
                <span style={{ flex:1, fontSize:'0.85rem', color:'var(--text)', fontWeight:600 }}>{createdCreds.email || '—'}</span>
                {createdCreds.email && (
                  <button style={iconBtn} onClick={() => copyToClipboard(createdCreds.email, 'cred-email')}>
                    {copied['cred-email'] ? '✓' : '⧉'}
                  </button>
                )}
              </div>
              <div style={credRow}>
                <span style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:70 }}>Password</span>
                <span style={{ flex:1, fontSize:'0.85rem', color:'var(--text)', fontWeight:600 }}>{createdCreds.password}</span>
                <button style={iconBtn} onClick={() => copyToClipboard(createdCreds.password, 'cred-pw')}>
                  {copied['cred-pw'] ? '✓' : '⧉'}
                </button>
              </div>
              <div className="modal-footer" style={{ padding:'0', border:'none', marginTop:16 }}>
                <button className="btn btn-primary" onClick={() => setCreatedCreds(null)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── helpers ── */
function statusStyle(avail) {
  if (avail === 'available') return { dot:'#22c55e', badge:'#16a34a', badgeBg:'#dcfce7' };
  if (avail === 'busy')      return { dot:'#f59e0b', badge:'#b45309', badgeBg:'#fef3c7' };
  return                            { dot:'#ef4444', badge:'#b91c1c', badgeBg:'#fee2e2' };
}

function chipStyle(color, bg) {
  return {
    display:'inline-flex', alignItems:'center', gap:5,
    padding:'5px 14px', borderRadius:20,
    background: bg, color, fontWeight:700,
    fontSize:'0.78rem', border:`1px solid ${color}44`,
  };
}

function actionBtn(active, color) {
  return {
    width:30, height:30, borderRadius:'50%',
    border: active ? `2px solid ${color}` : '1.5px solid var(--border)',
    background: active ? `${color}18` : 'var(--card)',
    color: active ? color : 'var(--text-muted)',
    cursor:'pointer', fontSize:'0.82rem', fontWeight:700,
    display:'flex', alignItems:'center', justifyContent:'center',
    transition:'all 0.15s',
  };
}

const iconBtn = {
  background:'transparent', border:'none', cursor:'pointer',
  color:'var(--text-muted)', fontSize:'0.8rem', padding:'0 2px', lineHeight:1,
};

const credRow = {
  display:'flex', alignItems:'center', gap:8,
  background:'var(--bg)', borderRadius:8,
  padding:'8px 12px', marginBottom:8,
  border:'1px solid var(--border)',
};
